/**
 * Created by neo on 18.01.17.
 */
import { makeAutoObservable, reaction, IReactionDisposer } from 'mobx';
import { ExerciseVariation } from '../ProgramPortfolio/ExerciseVariation';
import * as mobxUtils from 'mobx-utils';
import { CoachWorkoutPhase } from '../Coach/Program/Workout/CoachWorkoutPhase';
import { CoachExerciseBlock, createCoachExerciseBlock } from '../Coach/Program/Workout/CoachExerciseBlock';
import { logger } from '../../Utils/logger';
import type { ViewWorkout } from './ViewWorkout';
import { ExerciseBlockLog } from './ExerciseBlockLog';
import { ViewBlock, createViewBlock } from './ViewBlock';
import type { ViewSet } from './ViewSet';

export type ViewPhase = {
  viewWorkout: ViewWorkout;
  blocks: ExerciseBlockLog[];
  phase: CoachWorkoutPhase;
  exerciseBlocks: ViewBlock[];
  elapsedTime: number;
  elapsedDisposer?: IReactionDisposer;
  readonly viewPhaseId: string;
  readonly prevViewPhase: ViewPhase | undefined;
  readonly nextViewPhase: ViewPhase | undefined;
  readonly viewPhaseIndex: number;
  readonly type: string;
  readonly description: string;
  readonly isCompleted: boolean;
  readonly blockCount: number;
  readonly completedBlocks: ViewBlock[];
  readonly completedOrStartedBlocks: ViewBlock[];
  readonly completedExercises: number;
  readonly completedOrStartedExercises: number;
  readonly exercises: ExerciseVariation[];
  readonly firstBlock: ViewBlock | undefined;
  readonly lastBlock: ViewBlock | undefined;
  readonly startTimestamp: number;
  readonly firstSet: ViewSet | undefined;
  readonly lastSet: ViewSet | undefined;
  readonly isStarted: boolean;
  readonly isRunning: boolean;
  readonly duration: number;
  readonly endTimestamp: number;
  readonly prevBlock: ViewBlock | undefined;
  readonly nextBlock: ViewBlock | undefined;
  readonly isLastPhase: boolean;
  readonly lastViewBlock: ViewBlock | undefined;
  readonly isUnplanned: boolean;
  startTimer(): void;
  stopTimer(): void;
  accepts(block: ExerciseBlockLog): boolean;
  hasExercise(exercise: ExerciseVariation, exerciseBlockId?: string): boolean;
  replay(): void;
  replaceBlock(oldBlock: ViewBlock, newBlock: CoachExerciseBlock): Promise<ViewSet | undefined>;
};

export function createViewPhase(viewWorkout: ViewWorkout, phase: CoachWorkoutPhase): ViewPhase {
  return makeAutoObservable({
    viewWorkout,
    blocks: [] as ExerciseBlockLog[],
    phase,
    exerciseBlocks: [] as ViewBlock[],
    elapsedTime: 0,
    elapsedDisposer: undefined as IReactionDisposer | undefined,

    startTimer() {
      this.elapsedDisposer?.();
      this.elapsedDisposer = reaction(
        () => mobxUtils.now(40),
        (timestamp: number) => {
          if (this.isRunning && timestamp) {
            this.elapsedTime = timestamp - this.startTimestamp;
          }
        },
        { name: 'ViewPhase::elapsedTime' },
      );
    },

    stopTimer() {
      this.elapsedDisposer?.();
      this.elapsedDisposer = undefined;
    },

    accepts(block: ExerciseBlockLog): boolean {
      if (block.phaseId === this.phase.phaseId ||
        (!this.hasExercise(block.exercise, block.exerciseBlockId) &&
          this.phase.hasExercise(block.exercise, block.exerciseBlockId))) {
        this.blocks.push(block);
        return true;
      }
      return false;
    },

    hasExercise(exercise: ExerciseVariation, exerciseBlockId?: string): boolean {
      for (const block of this.blocks) {
        if ((exerciseBlockId && block.exerciseBlockId === exerciseBlockId) ||
          (!exerciseBlockId && block.exercise.id === exercise.id)) {
          return true;
        }
      }
      return false;
    },

    replay() {
      const ignoreIndex = new Array(this.blocks.length);

      const assignBlockLog = (viewBlock: ViewBlock): boolean => {
        for (let i = this.blocks.length - 1; i >= 0; i -= 1) {
          if (!ignoreIndex[i] && viewBlock.accepts(this.blocks[i])) {
            ignoreIndex[i] = true;
            return true;
          }
        }
        return false;
      };

      const viewBlocks = this.phase.exerciseBlocks.map((b: CoachExerciseBlock) => {
        const viewBlock = createViewBlock(this, b);
        assignBlockLog(viewBlock);
        viewBlock.replay();
        return viewBlock;
      });

      this.exerciseBlocks = viewBlocks.concat(
        this.blocks
          .filter((_, index) => !ignoreIndex[index])
          .map((log) => {
            const viewBlock = createViewBlock(
              this,
              createCoachExerciseBlock(this.phase, { exercise: log.exercise, exerciseBlockId: log.exerciseBlockId }),
              log,
            );
            viewBlock.replay();
            return viewBlock;
          }),
      );
    },

    replaceBlock(oldBlock: ViewBlock, newBlock: CoachExerciseBlock): Promise<ViewSet | undefined> {
      this.phase.replaceBlock(oldBlock.block, newBlock);
      const index = this.exerciseBlocks.findIndex((b) => b.block.exerciseBlockId === oldBlock.block.exerciseBlockId);
      oldBlock.sets = oldBlock.sets.filter((s) => s.isFinished);
      logger('replaceBlock', oldBlock.exercise.name, oldBlock.sets.length);

      const newViewBlock = createViewBlock(this, newBlock);
      newViewBlock.replay();

      if (index !== -1) {
        if (oldBlock.sets.length === 0) {
          const logIndex = this.viewWorkout.workoutLog.exerciseBlocks.findIndex(
            (b) => b.exerciseBlockId === oldBlock.logBlock?.exerciseBlockId,
          );
          if (logIndex !== -1) this.viewWorkout.workoutLog.exerciseBlocks.splice(logIndex, 1);
          this.exerciseBlocks[index] = newViewBlock;
        } else if (index < this.exerciseBlocks.length - 1) {
          this.exerciseBlocks.splice(index + 1, 0, newViewBlock);
        } else {
          this.exerciseBlocks.push(newViewBlock);
        }
      } else {
        this.exerciseBlocks.push(newViewBlock);
      }

      return oldBlock.block.replaceRemote(newBlock).then(() => newViewBlock.firstSet);
    },

    get viewPhaseId(): string {
      return this.phase.phaseId;
    },

    get prevViewPhase(): ViewPhase | undefined {
      if (this.viewPhaseIndex > 0) return this.viewWorkout.viewPhases[this.viewPhaseIndex - 1];
      return undefined;
    },

    get nextViewPhase(): ViewPhase | undefined {
      const nextIndex = this.viewPhaseIndex + 1;
      return nextIndex < this.viewWorkout.viewPhases.length ? this.viewWorkout.viewPhases[nextIndex] : undefined;
    },

    get viewPhaseIndex(): number {
      return this.viewWorkout.viewPhases.findIndex((p) => p === this);
    },

    get type(): string {
      return this.phase.type;
    },

    get description(): string {
      return this.phase.translatedDescription || '';
    },

    get isCompleted(): boolean {
      return !this.exerciseBlocks.find((b) => !b.completed);
    },

    get blockCount(): number {
      return this.exerciseBlocks.length;
    },

    get completedBlocks(): ViewBlock[] {
      return this.exerciseBlocks.filter((b) => b.completed);
    },

    get completedOrStartedBlocks(): ViewBlock[] {
      return this.exerciseBlocks.filter((b) => b.hasCompletedSets);
    },

    get completedExercises(): number {
      return this.exerciseBlocks.reduce((total: number, block: ViewBlock) => total + (block.completed ? 1 : 0), 0);
    },

    get completedOrStartedExercises(): number {
      return this.completedOrStartedBlocks.length;
    },

    get exercises(): ExerciseVariation[] {
      return this.exerciseBlocks.map((b: ViewBlock) => b.exercise);
    },

    get firstBlock(): ViewBlock | undefined {
      return this.exerciseBlocks[0];
    },

    get lastBlock(): ViewBlock | undefined {
      return this.exerciseBlocks.length > 0 ? this.exerciseBlocks[this.exerciseBlocks.length - 1] : undefined;
    },

    get startTimestamp(): number {
      return this.firstSet?.logSet?.startDateTime?.valueOf() ?? 0;
    },

    get firstSet(): ViewSet | undefined {
      return this.firstBlock?.firstSet;
    },

    get lastSet(): ViewSet | undefined {
      return this.lastBlock?.lastSet;
    },

    get isStarted(): boolean {
      return this.firstSet?.isStarted ?? false;
    },

    get isRunning(): boolean {
      if (this.firstSet?.isStarted) return !this.lastSet || !this.lastSet.isFinished;
      return false;
    },

    get duration(): number {
      if (this.isRunning) return this.elapsedTime;
      if (this.endTimestamp !== 0) return Math.abs(this.endTimestamp - this.startTimestamp);
      return 0;
    },

    get endTimestamp(): number {
      return this.lastSet?.logSet?.endDateTime?.valueOf() ?? 0;
    },

    get prevBlock(): ViewBlock | undefined {
      return this.lastBlock || this.prevViewPhase?.prevBlock;
    },

    get nextBlock(): ViewBlock | undefined {
      return this.firstBlock || this.nextViewPhase?.nextBlock;
    },

    get isLastPhase(): boolean {
      return !this.nextViewPhase;
    },

    get lastViewBlock(): ViewBlock | undefined {
      return this.exerciseBlocks.slice(-1).pop();
    },

    get isUnplanned(): boolean {
      return this.viewPhaseId.toLowerCase() === 'unplanned';
    },
  });
}
